import { useContext, useEffect, useState } from 'react';
import { ApiContext } from '../App';
import { useQuery } from '@tanstack/react-query';

import { getUser } from '../lib/actions/users';
import {
  getCompanyApplications,
  getCompanyDocuments,
  getCompanyStats,
} from '../lib/actions/companies';

function useCompanyDependencies(company) {
  let companyProps;
  const api = useContext(ApiContext);
  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fetchConfig = {
    enabled: !!company && !companyProps,
    retry: false,
  };

  const {
    data: user,
    error: userError,
    isLoading: userLoading,
  } = useQuery({
    queryKey: ['users', company?.id],
    queryFn: () => getUser(api, company?.borrower.id),
    ...fetchConfig,
  });

  const {
    data: applications,
    error: applicationsError,
    isLoading: applicationsLoading,
  } = useQuery({
    queryKey: ['applications', company?.id],
    queryFn: () => getCompanyApplications(api, company),
    ...fetchConfig,
  });

  const {
    data: stats,
    error: statsError,
    isLoading: statsLoading,
  } = useQuery({
    queryKey: ['stats', company?.id],
    queryFn: () => getCompanyStats(api, company?.id),
    ...fetchConfig,
  });

  const {
    data: companyDocs,
    error: companyDocsError,
    isLoading: companyDocsLoading,
  } = useQuery({
    queryKey: ['companyDocs', company?.id],
    queryFn: () => getCompanyDocuments(api, company),
    ...fetchConfig,
  });

  useEffect(() => {
    setHasErrors(
      userError || applicationsError || statsError || companyDocsError,
    );
  }, [userError, applicationsError, statsError, companyDocsError]);

  useEffect(() => {
    setIsLoading(
      userLoading || applicationsLoading || statsLoading || companyDocsLoading,
    );
  }, [userLoading, applicationsLoading, statsLoading, companyDocsLoading]);

  const errors = {
    userError,
    applicationsError,
    statsError,
    documentError: companyDocsError,
  };

  companyProps = {
    type: 'company',
    entity: company,
    ownerAddress: user?.attributes?.address,
    applications: applications?.map((app) => app.attributes),
    stats: stats,
    documents: companyDocs,
    errors,
    hasErrors,
    isLoading,
  };

  return { companyProps };
}

export default useCompanyDependencies;
